import { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * The cart, which IS the hold — and what a hold that ran out let go of.
 *
 * `expires_at` is the ten minutes running down, set once when the FIRST line
 * goes in and never extended — adding more does not buy more time. The clock
 * therefore rides the primary button of every screen from that moment, so it
 * reads the same wherever you are.
 *
 * The hold is advisory. Two parents can both be looking at the same unclaimed
 * hour and both be told yes; the first to check out claims it and the second is
 * told it has gone. That is why checkout can refuse with the slots that went.
 *
 * `released` is the other half: at ten minutes the hold lapses and the parent
 * used to be told nothing at all. It is a separate list rather than a flag on
 * the cart, because a parent can be holding one hour and mourning two others at
 * the same moment, and one object cannot say that.
 *
 * Consumed through `CartProvider`, not directly — see `cart-provider.tsx` for
 * why.
 */

export interface CartLine {
  cart_item_id: string;
  published_slot_id: string;
  athlete_client_id: string;
  athlete_name: string;
  service_id: string;
  capacity_tier_id: string;
  slot_start: string;
  slot_end: string;
  clinic_title: string | null;
}

export interface Cart {
  cart_id: string;
  expires_at: string;
  lines: CartLine[];
}

/**
 * One child, one hour, released when the hold ran out.
 *
 * No `cart_item_id`: dismissing one deletes nothing server-side, so there is no
 * id worth holding. The pair that identifies it is the slot and the child.
 */
export interface ReleasedLine {
  published_slot_id: string;
  athlete_client_id: string;
  athlete_name: string;
  service_id: string;
  capacity_tier_id: string;
  slot_start: string;
  slot_end: string;
}

export type CartState =
  | { status: 'loading' }
  | { status: 'ready'; cart: Cart | null; released: ReleasedLine[] }
  | { status: 'failed'; message: string };

export function useCart(): { state: CartState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<CartState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    setState({ status: 'loading' });

    apiFetch<{ cart: Cart | null; released?: ReleasedLine[] }>('/api/cart', getToken)
      .then((body) => {
        // `released` is defaulted rather than required, so a portal deployed
        // ahead of the API reads as "nothing released" instead of throwing on
        // every page.
        if (!cancelled) setState({ status: 'ready', cart: body.cart, released: body.released ?? [] });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) return;
        setState({
          status: 'failed',
          message:
            cause instanceof ApiError
              ? cause.message
              : 'The API did not answer. Is the Functions host running on port 7071?',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, nonce]);

  return { state, reload };
}

export interface CartLineRequest {
  publishedSlotId: string;
  athleteClientId: string;
  serviceId: string;
  capacityTierId: string;
  /**
   * The current waiver, approved on the hour screen with the words it showed.
   * Only for a child whose signature is not on it; the API signs it with the line.
   */
  consent?: { waiverId: string; text: string } | null;
}

/**
 * One line per athlete per hour, which is what the API takes.
 *
 * Two children in one hour is two calls. They are made in sequence rather than
 * at once: the second depends on the first having claimed the hour as the right
 * product, and firing them together would race for the same seats.
 */
export async function addCartLine(
  getToken: () => Promise<string | null>,
  line: CartLineRequest,
): Promise<{ cart_id: string; cart_item_id: string; expires_at: string }> {
  return apiFetch('/api/cart/items', getToken, {
    method: 'POST',
    body: JSON.stringify({
      published_slot_id: line.publishedSlotId,
      athlete_client_id: line.athleteClientId,
      service_id: line.serviceId,
      capacity_tier_id: line.capacityTierId,
      consent: line.consent ? { waiver_id: line.consent.waiverId, text: line.consent.text } : null,
    }),
  });
}

export async function removeCartLine(
  getToken: () => Promise<string | null>,
  cartItemId: string,
): Promise<{ remaining: number }> {
  return apiFetch(`/api/cart/items/${cartItemId}`, getToken, { method: 'DELETE' });
}

export interface BookedSession {
  booking_id: string;
  /** One per athlete on the booking. */
  lessons: { lesson_id: string; athlete_client_id: string }[];
  slot_id: string;
  slot_start: string;
  attendee_client_ids: string[];
}

/**
 * All or nothing. If an hour went while the parent was choosing, this throws
 * with `slots_unavailable` and the slot ids, and NOTHING is booked — they take
 * that line out and the rest go through together.
 */
export async function checkout(
  getToken: () => Promise<string | null>,
): Promise<BookedSession[]> {
  const body = await apiFetch<{ bookings: BookedSession[] }>('/api/cart/checkout', getToken, {
    method: 'POST',
  });
  return body.bookings;
}

/** Whole seconds left on the hold, floored at zero. */
export function secondsLeft(expiresAt: string, now: number = Date.now()): number {
  const left = Math.floor((new Date(expiresAt).getTime() - now) / 1000);
  return left > 0 ? left : 0;
}

/** m:ss, which is how a countdown under ten minutes reads. */
export function formatClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}
